import { X } from 'lucide-react'
import { useState, useEffect } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateCashFlow,
  useUpdateCashFlow,
  type CashFlowWithRelations,
  type CreateCashFlowInput,
  type AccountWithBalance,
  type CategoryTree,
} from '@/hooks'


interface CashFlowFormProps {
  cashFlow?: CashFlowWithRelations
  accounts: AccountWithBalance[]
  categories?: CategoryTree
  onClose: () => void
  onSuccess: () => void
}

export function CashFlowForm({
  cashFlow,
  accounts,
  categories,
  onClose,
  onSuccess,
}: CashFlowFormProps) {
  const isEditing = !!cashFlow
  const activeAccounts = accounts.filter((a) => a.isActive)
  const defaultAccount = activeAccounts[0]

  const [formData, setFormData] = useState<CreateCashFlowInput>({
    accountId: cashFlow?.accountId ?? defaultAccount?.id ?? '',
    categoryId: cashFlow?.categoryId ?? '',
    type: cashFlow?.type ?? 'expense',
    amount: cashFlow?.amount ?? 0,
    currency: cashFlow?.currency ?? defaultAccount?.currency ?? 'USDT',
    flowDate: cashFlow?.flowDate ?? new Date().toISOString().split('T')[0],
    description: cashFlow?.description ?? '',
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateCashFlow()
  const updateMutation = useUpdateCashFlow()

  // 当选择账户时，自动更新币种
  useEffect(() => {
    if (formData.accountId) {
      const selectedAccount = accounts.find((a) => a.id === formData.accountId)
      if (selectedAccount && selectedAccount.currency !== formData.currency) {
        setFormData((prev) => ({ ...prev, currency: selectedAccount.currency }))
      }
    }
  }, [formData.accountId, formData.currency, accounts])

  // 获取当前选中账户的币种
  const selectedAccount = accounts.find((a) => a.id === formData.accountId)
  const currentCurrency = selectedAccount?.currency ?? formData.currency

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.accountId) {
      setError('请选择账户')
      return
    }
    if (!formData.categoryId) {
      setError('请选择分类')
      return
    }

    const submitAsync = async () => {
      try {
        if (isEditing && cashFlow) {
          await updateMutation.mutateAsync({
            id: cashFlow.id,
            data: {
              accountId: formData.accountId,
              categoryId: formData.categoryId,
              type: formData.type,
              amount: Math.round(formData.amount * 100),
              flowDate: formData.flowDate,
              description: formData.description || undefined,
            },
          })
        } else {
          await createMutation.mutateAsync({
            ...formData,
            amount: Math.round(formData.amount * 100),
          })
        }
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : '操作失败')
      }
    }

    void submitAsync()
  }

  const isPending = createMutation.isPending || updateMutation.isPending
  const categoryList = formData.type === 'income' ? categories?.income : categories?.expense

  // 扁平化分类列表
  const flattenCategories = (
    cats: typeof categoryList,
    level = 0
  ): Array<{ id: string; name: string; level: number }> => {
    if (!cats) {
      return []
    }
    return cats.flatMap((cat) => [
      { id: cat.id, name: cat.name, level },
      ...flattenCategories(cat.children, level + 1),
    ])
  }

  const flatCategories = flattenCategories(categoryList)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEditing ? '编辑现金流' : '新建现金流'}</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">类型</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, type: 'income', categoryId: '' }))}
                className={`flex-1 rounded-md border px-4 py-2 text-sm ${formData.type === 'income'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'hover:bg-muted'
                  }`}
              >
                收入
              </button>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, type: 'expense', categoryId: '' }))}
                className={`flex-1 rounded-md border px-4 py-2 text-sm ${formData.type === 'expense'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'hover:bg-muted'
                  }`}
              >
                支出
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">账户</label>
            <Select
              value={formData.accountId}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, accountId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择账户" />
              </SelectTrigger>
              <SelectContent>
                {activeAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} ({account.currency})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentCurrency && (
              <p className="mt-1 text-xs text-muted-foreground">
                币种: {currentCurrency}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">分类</label>
            <Select
              value={formData.categoryId}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, categoryId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="请选择分类" />
              </SelectTrigger>
              <SelectContent>
                {flatCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {'　'.repeat(cat.level)}
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">
              金额 {currentCurrency && `(${currentCurrency})`}
            </label>
            <input
              type="number"
              step="0.01"
              value={isEditing ? formData.amount / 100 : formData.amount}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  amount: isEditing
                    ? Math.round(parseFloat(e.target.value || '0') * 100)
                    : parseFloat(e.target.value || '0'),
                }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">日期</label>
            <input
              type="date"
              value={formData.flowDate}
              onChange={(e) => setFormData((prev) => ({ ...prev, flowDate: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">描述</label>
            <textarea
              value={formData.description ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="可选描述"
              rows={2}
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
